import { createContext, useContext } from 'react'

export interface FieldContextValue {
  id: string
  hintId?: string
  errorId?: string
  invalid: boolean
  disabled?: boolean
  required?: boolean
}

export const FieldContext = createContext<FieldContextValue | null>(null)

export interface FieldControlProps {
  id?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  'aria-required'?: boolean
  disabled?: boolean
}

/**
 * Accessibility wiring for a control rendered inside <Field>: id (label htmlFor target),
 * aria-describedby (hint and error) and aria-invalid. Explicit props on the control win.
 */
export function useFieldControl(own: FieldControlProps = {}): FieldControlProps & { invalid: boolean } {
  const field = useContext(FieldContext)
  const describedBy =
    [own['aria-describedby'], field?.errorId, field?.hintId].filter(Boolean).join(' ') || undefined
  const invalid = Boolean(own['aria-invalid'] ?? field?.invalid)
  return {
    id: own.id ?? field?.id,
    'aria-describedby': describedBy,
    'aria-invalid': invalid || undefined,
    'aria-required': own['aria-required'] ?? (field?.required || undefined),
    disabled: own.disabled ?? field?.disabled,
    invalid,
  }
}
